import { useEffect, useState, type ReactNode } from 'react';
import { prepareSurveillanceSession } from './auth';

export function sameOriginSurveillance(url?: string): boolean {
  try {
    const target = new URL(url ?? '', window.location.origin);
    return !!url && target.origin === window.location.origin && target.pathname === '/surveillance/';
  } catch { return false; }
}

/** Children (including iframe) never mount before the server mints the session. */
export function SurveillanceSessionGate({ children }: { children: ReactNode }): JSX.Element {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let expiry: ReturnType<typeof setTimeout> | undefined;
    let ownerSignal: AbortSignal | undefined;
    const stop = () => { setReady(false); controller.abort(); clearTimeout(timer); clearTimeout(expiry); };
    const mint = async () => {
      try {
        const lease = await prepareSurveillanceSession(controller.signal);
        if (controller.signal.aborted) return;
        const remaining = lease.deadline - performance.now();
        if (lease.signal.aborted || remaining <= 0) { stop(); return; }
        ownerSignal?.removeEventListener('abort', stop);
        ownerSignal = lease.signal;
        ownerSignal.addEventListener('abort', stop, { once: true });
        clearTimeout(expiry);
        expiry = setTimeout(stop, remaining);
        setReady(true);
        timer = setTimeout(() => { void mint(); }, Math.min(5 * 60_000, remaining / 2));
      } catch { if (!controller.signal.aborted) stop(); }
    };
    void mint();
    return () => { controller.abort(); clearTimeout(timer); clearTimeout(expiry); ownerSignal?.removeEventListener('abort', stop); };
  }, []);
  return ready ? <>{children}</> : <p role="status">Kamera-Sitzung wird geprüft. Bei fehlgeschlagener Prüfung bitte erneut öffnen.</p>;
}
